import { fetchEventSource } from "@microsoft/fetch-event-source";
import { env } from "@/lib/env";
import { NOTIFICATION_TYPES, type ConnectionStatus, type NotificationType } from "../types";
import type {
  ConnectionStatusHandler,
  GetTokenFn,
  HeartbeatHandler,
  NotificationSignalHandler,
  NotificationsClient,
  RefreshSessionFn,
} from "./types";

/**
 * URL do stream SSE. Preferimos a base ABSOLUTA do serviço central de
 * notificações (`VITE_NOTIFICATIONS_API_BASE_URL`, que já inclui `/v1`). Sem ela,
 * caímos no comportamento anterior: a base absoluta da API do backoffice
 * (`VITE_API_BASE_URL`, também com `/v1`), igual ao cliente REST — o domínio do
 * web app não roteia `/v1` para a API (o REST vai direto por URL absoluta +
 * CORS), então um caminho relativo cairia no fallback do SPA (200 `text/html`) e
 * o SSE falharia com "open failed: 200". Sem nenhuma base (dev 100% local), cai
 * no caminho relativo coberto pelo proxy do Vite.
 */
const STREAM_URL = env.VITE_NOTIFICATIONS_API_BASE_URL
  ? `${env.VITE_NOTIFICATIONS_API_BASE_URL}/notifications/stream`
  : env.VITE_API_BASE_URL
    ? `${env.VITE_API_BASE_URL}/notifications/stream`
    : "/v1/notifications/stream";
const RECONNECT_BASE_MS = 2000;
const RECONNECT_MAX_MS = 30000;

/** Nomes dos eventos SSE do contrato "option C" (ADR-0025). */
const EVENT_NOTIFICATION = "notification";
const EVENT_HEARTBEAT = "heartbeat";
const EVENT_REAUTH = "reauth";

function isNotificationType(value: unknown): value is NotificationType {
  return typeof value === "string" && NOTIFICATION_TYPES.includes(value as NotificationType);
}

/** Espera cancelável: resolve antes se o sinal abortar (stop()/build() durante backoff). */
function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(done, ms);
    function done(): void {
      clearTimeout(timer);
      signal.removeEventListener("abort", done);
      resolve();
    }
    signal.addEventListener("abort", done);
  });
}

/**
 * Cliente de notificações em tempo real sobre Server-Sent Events
 * (`@microsoft/fetch-event-source`) apontado para o serviço central
 * `imobgo-notifications`. Recebe-only com contrato SLIM ("option C", ADR-0025): o
 * servidor emite o evento `notification` com uma dica `{id,type,unread}` (não o
 * `NotificationDto` completo), `heartbeat` periódico com `{unread}` para curar o
 * badge, e `reauth` quando o token está perto de expirar. A lista é reconciliada
 * via REST; ações de volta (marcar lida etc.) continuam via REST.
 */
class SseClient implements NotificationsClient {
  private controller: AbortController | null = null;
  private getToken: GetTokenFn = () => null;
  private refreshSession: RefreshSessionFn | null = null;
  /** Renovação de sessão em voo — deduplica `reauth` + reconexão concorrentes. */
  private renewing: Promise<string | null> | null = null;
  private status: ConnectionStatus = "disconnected";
  private statusHandlers: ConnectionStatusHandler[] = [];
  /** Handlers do evento slim `notification`, preservados entre reconstruções. */
  private notificationHandlers: NotificationSignalHandler[] = [];
  /** Handlers do `heartbeat`. */
  private heartbeatHandlers: HeartbeatHandler[] = [];
  /** Authorization corrente — relido do store antes de CADA tentativa de conexão. */
  private headers: Record<string, string> = {};
  private reconnectMs = RECONNECT_BASE_MS;

  build(getToken: GetTokenFn, refreshSession?: RefreshSessionFn): void {
    void this.stop();
    this.getToken = getToken;
    this.refreshSession = refreshSession ?? null;
  }

  async start(): Promise<void> {
    if (this.controller) return;

    const controller = new AbortController();
    this.controller = controller;
    this.reconnectMs = RECONNECT_BASE_MS;
    this.emitStatus("reconnecting");

    // Resolve na primeira abertura bem-sucedida; rejeita se a primeira tentativa
    // falhar (o loop continua reconectando em background nos dois casos).
    return new Promise<void>((resolve, reject) => {
      void this.runLoop(controller, resolve, reject);
    });
  }

  /**
   * Loop de reconexão PRÓPRIO — o retry interno do `fetchEventSource` não serve aqui:
   * a lib COPIA os headers no init (mutar o objeto depois nunca atualiza o
   * Authorization dos retries — reconexão ficava presa no token do connect, expirado
   * → 401 em loop até o F5) e um fechamento gracioso do servidor (reauth/teto)
   * resolve a promessa SEM reabrir. Então: cada (re)conexão é um fetchEventSource
   * novo com headers frescos; `onerror` relança para encerrá-lo e o loop decide —
   * fechamento gracioso ou 401 renovam a SESSÃO antes de reabrir.
   */
  private async runLoop(
    controller: AbortController,
    resolveFirstOpen: () => void,
    rejectFirstOpen: (err: Error) => void,
  ): Promise<void> {
    let firstAttempt = true;

    while (!controller.signal.aborted) {
      this.refreshAuth();
      let unauthorized = false;
      let failure: Error | null = null;

      try {
        await fetchEventSource(STREAM_URL, {
          signal: controller.signal,
          headers: this.headers,
          openWhenHidden: true,
          onopen: async (response) => {
            const contentType = response.headers.get("content-type") ?? "";
            if (response.ok && contentType.includes("text/event-stream")) {
              this.reconnectMs = RECONNECT_BASE_MS;
              this.emitStatus("connected");
              if (firstAttempt) {
                firstAttempt = false;
                resolveFirstOpen();
              }
              return;
            }
            unauthorized = response.status === 401;
            throw new Error(`SSE open failed: ${response.status}`);
          },
          onmessage: (ev) => this.dispatch(ev.event, ev.data),
          onerror: (err) => {
            // Sem retry interno: relança (fatal p/ a lib) e o loop externo reabre.
            throw err instanceof Error ? err : new Error(String(err));
          },
        });
        // Promessa resolveu: abort (stop/build) ou fechamento gracioso pelo servidor.
      } catch (err) {
        failure = err instanceof Error ? err : new Error(String(err));
      }

      if (controller.signal.aborted) break;
      this.emitStatus("reconnecting");

      if (firstAttempt) {
        firstAttempt = false;
        rejectFirstOpen(failure ?? new Error("SSE stream closed before first open"));
      }

      // Fechamento gracioso (servidor fecha perto do exp do token) ou 401: renova a
      // SESSÃO de verdade (POST /auth/refresh) — só reler o token em memória não
      // basta, ele pode já estar expirado.
      if (failure === null || unauthorized) {
        const alive = await this.renewSession();
        if (!alive) break;
        if (controller.signal.aborted) break;
      }

      await delay(this.reconnectMs, controller.signal);
      this.reconnectMs = Math.min(this.reconnectMs * 2, RECONNECT_MAX_MS);
    }

    // Encerramento decidido pelo próprio loop (sessão morta): emite o estado final.
    // No abort via stop()/build() o controller já foi trocado e o status emitido.
    if (this.controller === controller) {
      this.controller = null;
      this.emitStatus("disconnected");
    }
  }

  /**
   * Renova a sessão via authStore (single-flight — o refresh cookie é rotacionado
   * no primeiro uso; chamadas concorrentes compartilham a mesma promessa) e
   * atualiza o Authorization. Retorna false quando a sessão morreu: o authStore já
   * limpou o token e o hook (`hasToken`) derruba o cliente.
   */
  private async renewSession(): Promise<boolean> {
    if (!this.refreshSession) {
      this.refreshAuth();
      return this.getToken() !== null;
    }
    this.renewing ??= this.refreshSession().finally(() => {
      this.renewing = null;
    });
    const token = await this.renewing;
    this.refreshAuth();
    return token !== null;
  }

  async stop(): Promise<void> {
    if (!this.controller) return;
    this.controller.abort();
    this.controller = null;
    this.emitStatus("disconnected");
    return Promise.resolve();
  }

  onNotification(handler: NotificationSignalHandler): void {
    this.notificationHandlers.push(handler);
  }

  onHeartbeat(handler: HeartbeatHandler): void {
    this.heartbeatHandlers.push(handler);
  }

  getConnectionState(): ConnectionStatus {
    return this.status;
  }

  onStatusChange(handler: ConnectionStatusHandler): void {
    this.statusHandlers.push(handler);
  }

  private refreshAuth(): void {
    const token = this.getToken();
    this.headers.Authorization = token ? `Bearer ${token}` : "";
  }

  /**
   * Roteia por NOME do evento (contrato option C):
   * - `notification` → `{id,type,unread}` slim → handlers de notificação;
   * - `heartbeat` → `{unread}` → handlers de heartbeat (cura o badge);
   * - `reauth` → renova a SESSÃO já (antecipa o POST /auth/refresh; o loop reabre
   *   com o token novo após o fechamento — single-flight com a renovação do loop);
   * - qualquer outro (comentário `: connected`, `ping`, sem dado) → ignorado.
   */
  private dispatch(event: string | undefined, data: string): void {
    switch (event) {
      case EVENT_NOTIFICATION:
        this.handleNotification(data);
        return;
      case EVENT_HEARTBEAT:
        this.handleHeartbeat(data);
        return;
      case EVENT_REAUTH:
        void this.renewSession();
        return;
      default:
        return;
    }
  }

  private handleNotification(data: string): void {
    if (!data) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(data);
    } catch (err) {
      console.error("[sse] payload inválido para notification:", err);
      return;
    }
    if (typeof parsed !== "object" || parsed === null) return;
    const candidate = parsed as Record<string, unknown>;
    if (
      typeof candidate.id !== "string" ||
      !isNotificationType(candidate.type) ||
      typeof candidate.unread !== "number"
    ) {
      console.error("[sse] notification com formato inesperado:", candidate);
      return;
    }
    const signal = { id: candidate.id, type: candidate.type, unread: candidate.unread };
    for (const handler of this.notificationHandlers) {
      try {
        handler(signal);
      } catch (err) {
        console.error("[sse] notification handler threw:", err);
      }
    }
  }

  private handleHeartbeat(data: string): void {
    if (!data) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(data);
    } catch (err) {
      console.error("[sse] payload inválido para heartbeat:", err);
      return;
    }
    if (typeof parsed !== "object" || parsed === null) return;
    const unread = (parsed as Record<string, unknown>).unread;
    if (typeof unread !== "number") return;
    for (const handler of this.heartbeatHandlers) {
      try {
        handler(unread);
      } catch (err) {
        console.error("[sse] heartbeat handler threw:", err);
      }
    }
  }

  private emitStatus(status: ConnectionStatus): void {
    this.status = status;
    for (const h of this.statusHandlers) {
      try {
        h(status);
      } catch (err) {
        console.error("[sse] status handler threw:", err);
      }
    }
  }
}

export const sseClient: NotificationsClient = new SseClient();
